import { previewLLMCache, getFilenameFromPath } from "./previewMisc";
import { getChatCompletion } from "../../services/chatService";

const PROMPT_SECTIONS = {
  PREPROCESSING: "pre-processing",
  PRIMARY: "primary",
  POSTPROCESSING: "post-processing",
  VOICEMAIL: "voicemail",
};
const PROMPT_PATH = "prompt/";

export const getAllPromptNames = (processedFiles) => {
  const allPromptNames = [];

  Object.values(PROMPT_SECTIONS).forEach((section) => {
    if (!Array.isArray(processedFiles[section])) return;

    processedFiles[section].forEach((file) => {
      if (!file?.path) return;
      allPromptNames.push(getFilenameFromPath(file.path));
    });
  });

  return allPromptNames;
};

export const getSortedPromptNames = (allPromptNames, processedFiles) => {
  if (!Array.isArray(allPromptNames) || !processedFiles) {
    return [];
  }

  const cacheKey = JSON.stringify({
    promptNames: allPromptNames,
    fileKeys: Object.values(PROMPT_SECTIONS).map((section) =>
      Array.isArray(processedFiles[section])
        ? processedFiles[section].map((f) => f?.path).filter(Boolean)
        : []
    ),
  });

  if (previewLLMCache.sortedPromptNames.has(cacheKey)) {
    return previewLLMCache.sortedPromptNames.get(cacheKey);
  }

  const getSectionForPrompt = (name) => {
    if (!name) return "";

    for (const section of Object.values(PROMPT_SECTIONS)) {
      if (
        Array.isArray(processedFiles[section]) &&
        processedFiles[section].some(
          (file) => file && file.path && getFilenameFromPath(file.path) === name
        )
      ) {
        return section;
      }
    }
    return "";
  };

  const result = [...allPromptNames].sort((a, b) => {
    const sections = Object.values(PROMPT_SECTIONS);
    const aSection = getSectionForPrompt(a);
    const bSection = getSectionForPrompt(b);

    if (sections.indexOf(aSection) !== sections.indexOf(bSection)) {
      return sections.indexOf(aSection) - sections.indexOf(bSection);
    }
    return a.localeCompare(b);
  });

  previewLLMCache.sortedPromptNames.set(cacheKey, result);
  return result;
};

export const extractPromptInfoFromProperties = (
  promptProperties,
  processedFiles
) => {
  if (!promptProperties || !processedFiles) {
    console.warn(
      "Missing required parameters for extractPromptInfoFromProperties"
    );
    return {};
  }

  const scriptProperties = promptProperties["Script Properties"] || {};
  const promptInfo = {};
  const processedPaths = getAllProcessedPaths(processedFiles);
  const promptNames = processedPaths.map((path) => getFilenameFromPath(path));

  promptNames.forEach((promptName) => {
    if (!promptName) return;

    promptInfo[promptName] = createBasePromptInfo();

    processTypeProperty(scriptProperties, promptName, promptInfo);
    processSkillProperty(scriptProperties, promptName, promptInfo);
    processSkipLlmProperty(scriptProperties, promptName, promptInfo);
    processToolsProperty(scriptProperties, promptName, promptInfo);
  });

  return promptInfo;
};

function getAllProcessedPaths(processedFiles) {
  return [
    ...(Array.isArray(processedFiles.preprocessing)
      ? processedFiles.preprocessing
      : []),
    ...(Array.isArray(processedFiles.primary) ? processedFiles.primary : []),
    ...(Array.isArray(processedFiles.postprocessing)
      ? processedFiles.postprocessing
      : []),
    ...(Array.isArray(processedFiles.voicemail)
      ? processedFiles.voicemail
      : []),
  ]
    .filter((f) => f && f.path)
    .map((f) => f.path);
}

function createBasePromptInfo() {
  return {
    tools: [],
    toolDetails: [],
    type: "prompt",
    skill: "disabled",
    skipLlm: false,
  };
}

function processTypeProperty(scriptProperties, promptName, promptInfo) {
  const propertyKey = `${promptName}_type`;
  const property = scriptProperties[propertyKey];

  if (!property || !property.value) return;

  promptInfo[promptName].type = property.value;

  if (Array.isArray(property.menuItems)) {
    const typeItem = property.menuItems.find(
      (item) => item?.value === property.value
    );
    if (typeItem?.label) {
      promptInfo[promptName].typeLabel = typeItem.label;
    }
  }
}

function processSkillProperty(scriptProperties, promptName, promptInfo) {
  const propertyKey = `${promptName}_skill`;
  const property = scriptProperties[propertyKey];

  if (!property || !property.value) return;

  promptInfo[promptName].skill = property.value;

  if (Array.isArray(property.menuItems)) {
    const skillItem = property.menuItems.find(
      (item) => item?.value === property.value
    );
    if (skillItem) {
      promptInfo[promptName].skillLabel = skillItem.label;
      promptInfo[promptName].skillTooltip = skillItem.tooltip;
    }
  }
}

function processToolsProperty(scriptProperties, promptName, promptInfo) {
  const propertyKey = `${promptName}_tools`;
  const property = scriptProperties[propertyKey];

  if (!property || !property.value) return;

  const toolsList = property.value
    .split(",")
    .map((tool) => tool.trim())
    .filter(Boolean);

  promptInfo[promptName].tools = toolsList;

  if (toolsList.length > 0) {
    promptInfo[promptName].toolDetails = extractToolDetails(
      toolsList,
      property.checklistItems
    );
  }
}

function extractToolDetails(toolsList, checklistItems) {
  if (!Array.isArray(toolsList)) return [];

  return toolsList.map((tool) => {
    const defaultToolDetail = { name: tool, description: "" };

    if (!Array.isArray(checklistItems)) return defaultToolDetail;

    const checklistItem = checklistItems.find((item) => item?.value === tool);

    if (!checklistItem) return defaultToolDetail;

    return {
      name: tool,
      label: checklistItem.label,
      description: checklistItem.tooltip || "",
    };
  });
}

export const categorizePromptPaths = (contents) => {
  if (!contents)
    return {
      preprocessing: [],
      primary: [],
      postprocessing: [],
      voicemail: [],
    };

  const promptPaths = {
    preprocessing: [],
    primary: [],
    postprocessing: [],
    voicemail: [],
  };

  Object.keys(contents).forEach((path) => {
    if (!path) return;

    if (path.startsWith(`${PROMPT_PATH}${PROMPT_SECTIONS.PREPROCESSING}`)) {
      promptPaths.preprocessing.push(path);
    } else if (path.startsWith(`${PROMPT_PATH}${PROMPT_SECTIONS.PRIMARY}`)) {
      promptPaths.primary.push(path);
    } else if (
      path.startsWith(`${PROMPT_PATH}${PROMPT_SECTIONS.POSTPROCESSING}`)
    ) {
      promptPaths.postprocessing.push(path);
    } else if (path.startsWith(`${PROMPT_PATH}${PROMPT_SECTIONS.VOICEMAIL}`)) {
      promptPaths.voicemail.push(path);
    }
  });

  return promptPaths;
};

export const clearPromptProcessingCache = () => {
  previewLLMCache.clearCache();
};

export const processSkipLlmProperty = (
  scriptProperties,
  promptName,
  promptInfo
) => {
  const propertyKey = `${promptName}_skip_llm`;
  const property = scriptProperties[propertyKey];

  if (!property || !property.value) return;

  promptInfo[promptName].skipLlm =
    String(property.value).toLowerCase() === "true";
};

export const getCachedChatCompletion = async (params) => {
  const cacheKey = JSON.stringify({
    messages: params.messages,
    model: params.model,
    max_tokens: params.max_tokens,
    temperature: params.temperature,
    response_format: params.response_format,
  });

  if (previewLLMCache.chatCompletionResults.has(cacheKey)) {
    return previewLLMCache.chatCompletionResults.get(cacheKey);
  }

  try {
    const response = await getChatCompletion(params);

    if (response && response.data) {
      previewLLMCache.chatCompletionResults.set(cacheKey, response);
    }

    return response;
  } catch (error) {
    throw error;
  }
};
